import { Stack, Typography } from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { Sstv2TermLabel } from "./Sstv2TermLabel"
import { StreamProgress } from "./sstv2MigrationTypes"
import { describeStreamCounter } from "./sstv2Vocabulary"

/**
 * The counter breakdown under a stream's progress bar (DEV-193, D37 pass).
 *
 * ## Why the bar alone was not enough
 *
 * The bar showed `4/5 · 1 adopted · 1 failed` and nothing explained any of those
 * words. Two of them actively mislead a reader who has not read the architecture doc:
 *
 *  - **`adopted` reads as an error and is the opposite.** It means SL2 had no V1
 *    reservation to rewrite, so one was created from the partner's data — the only
 *    route available to that reservation, and it counts as DONE (D10). An operator
 *    seeing a number beside the word "adopted" next to a number beside the word
 *    "failed" reasonably concludes both are problems.
 *  - **`flipped` is not "created".** Migration rewrites the existing row in place
 *    (D2), so this number is not a count of new reservations and looking for new
 *    reservations to match it is a wasted hour.
 *
 * Each token therefore carries its gloss where it is rendered, via
 * `Sstv2TermLabel` — focusable, with the explanation in an `aria-label` so it is not
 * hover-only.
 *
 * ## Zeroes
 *
 * `adopted` and `failed` render only when non-zero: a row of zeroes trains the eye to
 * skip the line, which defeats the point of putting the explanation there. `received`
 * and `flipped` always render, because "0 flipped" is itself the signal the severe
 * empty-flip callout is about.
 */
export const Sstv2StreamLegend: FC<{ progress: StreamProgress }> = ({
  progress,
}) => {
  const { t } = useTranslation()

  if (progress.empty) {
    return (
      <Typography variant="caption" color="text.secondary">
        {t("migrationStreamNothingReceived")}
      </Typography>
    )
  }

  return (
    <Stack direction="row" spacing={1.5} flexWrap="wrap" rowGap={0.5}>
      <Sstv2TermLabel term={describeStreamCounter("received")} variant="caption">
        {t("migrationCounterReceived")} {progress.received}
      </Sstv2TermLabel>
      <Sstv2TermLabel
        term={describeStreamCounter("processed")}
        variant="caption"
      >
        {t("migrationCounterProcessed")} {progress.processed}
      </Sstv2TermLabel>
      {progress.adopted > 0 && (
        <Sstv2TermLabel
          term={describeStreamCounter("adopted")}
          variant="caption"
        >
          {t("migrationCounterAdopted")} {progress.adopted}
        </Sstv2TermLabel>
      )}
      {progress.failed > 0 && (
        <Sstv2TermLabel
          term={describeStreamCounter("failed")}
          variant="caption"
          color="error.main"
        >
          {t("migrationCounterFailed")} {progress.failed}
        </Sstv2TermLabel>
      )}
      {/* Only when a stamp EXISTS. An attempt from before the stamp shows nothing here
          rather than "certified 0", which would read as a count of zero work done
          instead of an absent measurement — the provenance chip carries that case. */}
      {progress.foldedCount !== undefined && (
        <Sstv2TermLabel
          term={describeStreamCounter("foldedCount")}
          variant="caption"
        >
          {t("migrationCounterFolded")} {progress.foldedCount}
        </Sstv2TermLabel>
      )}
    </Stack>
  )
}

export default Sstv2StreamLegend
